import * as changeCase from 'change-case';
import { useCampaignLeadResponseStats } from 'data/campaign-stats';
import { saveAs } from 'file-saver';
import * as jsonToCsv from 'json-2-csv';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { ELeadResponseType } from 'types/lead-response';
import { engagementLeadTypes, hotProspectLeadTypes, ILeadResponseData } from 'types/stats';

import { Box } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import GetAppIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VisibilityIcon from '@material-ui/icons/Visibility';

import Button from 'creative-components/CustomButtons/Button';
import ReactTable from 'creative-components/ReactTable/ReactTable';

import BadgedContacts from 'components/BadgedContacts';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import ViewEmailModal from 'components/ViewEmailModal';

import {
  enumToReadableName, formatPhoneNumber, getAgentLandingPageSampleUrlFromType, stringifyAddressObject, stringifyEventSiteAddressObject, tableFilterPhoneNumbers, transformObjectKeys,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
  },
  eventNameCell: {
    fontSize: '13px',
    fontWeight: 600,
  },
  campaignNameCell: {
    fontSize: '13px',
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'underline',

    '&:hover': {
      color: theme.palette.orange.main,
    },
  },
  noDataText: {
    marginTop: '60px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '36px',
    color: theme.palette.black.main,
  },
}));

const leadResponseTypeToHumanString = (type: ELeadResponseType): string => {
  if (type === ELeadResponseType.AgentLandingOpen) return 'QR Code Scanned';

  return enumToReadableName(type);
};

const tableFilterSiteInfo = (rows: any[], id: string, filterValue: string) => rows.filter((row) => {
  const combined = `${row.original.apn} ${row.original.siteAddress}`.toLowerCase();
  return combined.includes(filterValue.toLowerCase());
});

const tableFilterOwnerInfo = (rows: any[], id: string, filterValue: string) => rows.filter((row) => {
  const combined = `${row.original.ownerName} ${row.original.ownerAddress}`.toLowerCase();
  return combined.includes(filterValue.toLowerCase());
});

interface Props {
  campaign?: any; // TODO: ADD TYPE FOR CAMPAIGNS
  hotProspectsOnly: boolean;
}

export default ({ campaign, hotProspectsOnly }: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const { leadResponseStats } = useCampaignLeadResponseStats(campaign?._id);

  const [data, setData] = React.useState<any[] | null>(null);
  const [openModal, setOpenModal] = React.useState<React.FC | null>(null);

  const columns = React.useMemo(() => (
    [
      {
        minWidth: '150px',
        width: '150px',
        Header: 'Event',
        accessor: 'type',
        Cell: ({ cell: { value } }: any) => (
          <span className={classes.eventNameCell}>
            {value}
          </span>
        ),
      },
      ...(!campaign ? [{
        Header: 'Campaign Name',
        accessor: 'campaignName',
        Cell: ({ row }: any) => (
          <Link className={classes.campaignNameCell} to={`/dashboard/campaigns/${row.original.campaign._id}`}>
            {row.original.campaign.name}
          </Link>
        ),
      }] : []),
      {
        Header: 'Date',
        accessor: 'date',
      },
      {
        Header: 'Site Address',
        accessor: 'siteInfo',
        filter: tableFilterSiteInfo,
      },
      {
        Header: 'Owner Name & Address',
        accessor: 'ownerInfo',
        filter: tableFilterOwnerInfo,
      },
      {
        Header: 'Phone Numbers',
        accessor: 'phoneNumbers',
        filter: tableFilterPhoneNumbers,
        Cell: ({ value }: { value: string[] }) => <BadgedContacts main={value[0]} alternates={value.length > 1 ? value.slice(1) : []} />,
      },
      {
        Header: 'Emails',
        accessor: 'emails',
        Cell: ({ value }: { value: string[] }) => <BadgedContacts main={value[0]} alternates={value.length > 1 ? value.slice(1) : []} />,
      },
      {
        width: '100px',
        Header: '',
        accessor: 'actions',
      },
    ]
  ), [campaign]);

  React.useEffect(() => {
    if (!leadResponseStats) return;

    const leadResponsesSortedByDate = (hotProspectsOnly ? hotProspectLeadTypes : engagementLeadTypes)
      .reduce((arr, leadResponseType) => [...arr, ...leadResponseStats[leadResponseType]], [] as ILeadResponseData[])
      .sort((a, b) => b.date.localeCompare(a.date));

    setData(leadResponsesSortedByDate.map((leadResponse) => {
      // The user always receives touches themselves for free, and aren't technically an "extra recipient", but
      // for simplicity's sake, they are here.
      const {
        type, date, eventData, recipient, metadata,
      } = leadResponse;

      const { isExtraRecipient } = leadResponse.campaign;

      let ownerName: string;
      let phoneNumber: string | undefined;
      let email: string | undefined;
      let ownerAddress: string;

      if (eventData?.eventDataOverride) {
        ownerName = `${eventData.eventDataOverride.firstName || ''} ${eventData.eventDataOverride.lastName || ''}`; // || check here to prevent 'undefined' being concatenated
        phoneNumber = eventData.eventDataOverride.phoneNumber;
        email = eventData.eventDataOverride.email;
        ownerAddress = stringifyAddressObject(eventData.eventDataOverride.address);
      } else {
        ownerName = `${recipient.firstName || ''} ${recipient.lastName || ''}`; // || check here to prevent 'undefined' being concatenated
        phoneNumber = formatPhoneNumber(recipient.phoneNumber!); // Format these since they aren't formatted already when imported
        email = recipient.email;
        ownerAddress = stringifyAddressObject(recipient.address);
      }

      const { alternateEmails } = recipient;
      const alternatePhoneNumbers = (recipient.alternatePhoneNumbers ?? []).map((s) => formatPhoneNumber(s));

      return ({
        leadResponse, // We access this data when doing our custom filtering
        campaign: leadResponse.campaign, // Keep the full object for use in Cell renderer
        type: leadResponseTypeToHumanString(type),
        date: moment(date).format('MMM D, YYYY, h:mm A'),
        campaignName: leadResponse.campaign.name,
        apn: !isExtraRecipient ? eventData?.apn : 'N/A (Extra Recipient)', // For siteInfo filter function and exporting
        siteAddress: !isExtraRecipient ? stringifyEventSiteAddressObject(eventData?.siteAddress) : 'N/A (Extra Recipient)', // For siteInfo filter function and exporting
        siteInfo: (
          <>
            <div style={{ fontWeight: 600, marginBottom: '5px' }}>{!isExtraRecipient ? eventData?.apn : 'N/A (Extra Recipient)'}</div>
            {!isExtraRecipient ? stringifyEventSiteAddressObject(eventData?.siteAddress) : 'N/A (Extra Recipient)'}
          </>
        ),

        ownerName, // For ownerInfo filter function and exporting
        ownerAddress, // For ownerInfo filter function and exporting
        ownerInfo: (
          <>
            <div style={{ fontWeight: 600, marginBottom: '5px' }}>{ownerName}</div>
            {/* No address for the touches going to the agent directly, since a random campaign address is used and
            there's no address object tied to the agent's (the recipient) contact object. */}
            {!isExtraRecipient ? ownerAddress : 'Address N/A (Extra Recipient)'}
          </>
        ),
        phoneNumbers: [phoneNumber, ...(alternatePhoneNumbers ?? [])],
        emails: [email, ...(alternateEmails ?? [])],

        actions: (
          // Add a min height here for some padding
          <div style={{ minHeight: '41px' }}>
            {type === ELeadResponseType.EmailReply && metadata.relayData?.content?.html && (
              <Tooltip
                title={(
                  <>
                    View Email Reply
                  </>
                )}
                placement="top"
              >
                <Button
                  justIcon
                  round
                  simple
                  onClick={() => setOpenModal(
                    () => (
                      <ViewEmailModal
                        title="Email Reply"
                        emailSubject={metadata.relayData.content.subject}
                        emailHtml={metadata.relayData.content.html}
                        onClose={() => setOpenModal(null)}
                      />
                    ),
                  )}
                  color="dark"
                >
                  <VisibilityIcon />
                </Button>
              </Tooltip>
            )}

            {type !== ELeadResponseType.EmailClick && type !== ELeadResponseType.EmailReply && (
              <Tooltip
                title={(
                  <>
                    View QR Code
                    <br />
                    Landing Page
                  </>
              )}
                placement="top"
              >
                <Button
                  justIcon
                  round
                  simple
                  onClick={() => window.open(getAgentLandingPageSampleUrlFromType(leadResponse.campaign.agentLandingPageType, eventData?._id), '_blank', 'noopener,noreferrer')}
                  color="dark"
                >
                  <OpenInNewIcon />
                </Button>
              </Tooltip>
            )}
          </div>
        ),
      });
    }));
  }, [leadResponseStats]);

  const handleExportToCsv = async () => {
    const exportKeys = ['type', 'date', 'campaignName', 'apn', 'siteAddress', 'ownerName', 'ownerAddress', 'phoneNumbers', 'emails'];

    const csvData = await jsonToCsv.json2csv(
      data!
        .map((obj) => _.pick(obj, exportKeys))
        .map((obj) => Object.entries(transformObjectKeys(obj, changeCase.capitalCase)).reduce((newObj, [key, value]) => {
          // eslint-disable-next-line no-param-reassign
          newObj[key] = value === undefined ? '' : value;
          return newObj;
        }, {} as { [key: string]: any })),
    );

    saveAs(
      new Blob([csvData], { type: 'text/csv;charset=utf-8' }),
      `campaign-${campaign?._id ?? 'all'}-${hotProspectsOnly ? 'hot-prospects' : 'engagements'}-export.csv`,
    );
  };

  if (!data) return <LoadingIndicator />;

  return (
    <>
      {openModal}

      {data.length ? (
        <>
          <Box display="flex" justifyContent="space-between" flexDirection="row-reverse" alignItems="center" sx={{ mb: 2 }}>
            <Button
              round
              color="primary"
              onClick={handleExportToCsv}
            >
              <GetAppIcon />
              Export
            </Button>
          </Box>

          <div className={classes.tableContainer}>
            <ReactTable
              columns={columns}
              data={data}
            />
          </div>
        </>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center">
          <p className={classes.noDataText}>
            Once your campaign touches are delivered and we aggregate enough data, your events will appear here!
          </p>
        </Box>
      )}
    </>
  );
};
